import {
  Card,
  IndexTable,
  Badge,
  Pagination,
  Text,
  Button,
  InlineStack,
  BlockStack,
  Banner,
  useIndexResourceState,
} from '@shopify/polaris';
import { useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost } from '../api';

interface Subscription {
  id: string;
  customerId: string;
  shopifyContractId: string;
  status: string;
  lineItems: any;
  createdAt: string;
  customerEmail: string;
  customerFirstName: string | null;
  customerLastName: string | null;
}

interface SubsResponse {
  subscriptions: Subscription[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export default function SubscriptionsContent() {
  const [data, setData] = useState<SubsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [syncResult, setSyncResult] = useState<{ ok: boolean; message: string } | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiGet<SubsResponse>(
        `/panel-api/subscriptions?page=${page}&limit=50`,
      );
      setData(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = async (id: string, action: 'pause' | 'activate') => {
    setActionLoading(id);
    try {
      await apiPost(`/panel-api/subscriptions/${id}/${action}`);
      await fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSyncSellingPlan = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const result = await apiPost<{ ok: boolean; productsAssociated: number; sellingPlan: string; error?: string }>(
        '/panel-api/selling-plan/sync',
      );
      if (result.ok) {
        setSyncResult({ ok: true, message: `Selling plan aplicado a ${result.productsAssociated} productos` });
      } else {
        setSyncResult({ ok: false, message: result.error ?? 'Error desconocido' });
      }
    } catch (err: any) {
      setSyncResult({ ok: false, message: err.message });
    } finally {
      setSyncLoading(false);
    }
  };

  const handleRemoveSellingPlan = async () => {
    setRemoveLoading(true);
    setSyncResult(null);
    try {
      const result = await apiPost<{ ok: boolean; productsRemoved: number; error?: string }>(
        '/panel-api/selling-plan/remove',
      );
      if (result.ok) {
        setSyncResult({ ok: true, message: `Selling plan eliminado de ${result.productsRemoved} productos` });
      } else {
        setSyncResult({ ok: false, message: result.error ?? 'Error desconocido' });
      }
    } catch (err: any) {
      setSyncResult({ ok: false, message: err.message });
    } finally {
      setRemoveLoading(false);
    }
  };

  const subs = data?.subscriptions ?? [];
  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(subs as any);

  const rowMarkup = subs.map((sub, idx) => (
    <IndexTable.Row
      id={sub.id}
      key={sub.id}
      position={idx}
      selected={selectedResources.includes(sub.id)}
    >
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd" fontWeight="bold">
          {sub.customerFirstName ?? ''} {sub.customerLastName ?? ''}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{sub.customerEmail}</IndexTable.Cell>
      <IndexTable.Cell>
        <Badge tone={sub.status === 'active' ? 'success' : sub.status === 'paused' ? 'attention' : undefined}>
          {sub.status}
        </Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>{new Date(sub.createdAt).toLocaleDateString()}</IndexTable.Cell>
      <IndexTable.Cell>
        <InlineStack gap="200">
          {sub.status !== 'paused' && (
            <Button
              size="slim"
              onClick={() => handleAction(sub.id, 'pause')}
              loading={actionLoading === sub.id}
            >
              Pausar
            </Button>
          )}
          {sub.status !== 'active' && (
            <Button
              size="slim"
              variant="primary"
              onClick={() => handleAction(sub.id, 'activate')}
              loading={actionLoading === sub.id}
            >
              Activar
            </Button>
          )}
        </InlineStack>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <BlockStack gap="400">
      {/* Selling Plan Sync */}
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">Recompra fácil — Selling Plan</Text>
          <Text as="p" variant="bodySm" tone="subdued">
            Asocia el selling plan "Recompra bajo demanda" a todos los productos activos de la tienda.
            Necesario para que la extensión de checkout pueda activar la recompra.
          </Text>
          <InlineStack gap="300" align="start">
            <Button onClick={handleSyncSellingPlan} loading={syncLoading}>
              Aplicar a todos los productos
            </Button>
            <Button onClick={handleRemoveSellingPlan} loading={removeLoading} tone="critical">
              Eliminar de todos los productos
            </Button>
          </InlineStack>
          {syncResult && (
            <Banner tone={syncResult.ok ? 'success' : 'critical'} onDismiss={() => setSyncResult(null)}>
              <p>{syncResult.message}</p>
            </Banner>
          )}
        </BlockStack>
      </Card>

      {/* Subscriptions table */}
      <Card padding="0">
        <IndexTable
          resourceName={{ singular: 'suscripción', plural: 'suscripciones' }}
          itemCount={subs.length}
          selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
          onSelectionChange={handleSelectionChange}
          headings={[
            { title: 'Cliente' },
            { title: 'Email' },
            { title: 'Estado' },
            { title: 'Creada' },
            { title: 'Acciones' },
          ]}
          loading={loading}
        >
          {rowMarkup}
        </IndexTable>
      </Card>
      {data && data.pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
          <Pagination
            hasPrevious={page > 1}
            hasNext={page < data.pagination.totalPages}
            onPrevious={() => setPage(page - 1)}
            onNext={() => setPage(page + 1)}
          />
        </div>
      )}
    </BlockStack>
  );
}
